import { type ReactNode, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/layout/Layout";
import api from "./lib/api";
import { getGatewayNavigationUrl, isPublicEditorPath } from "./lib/backendUrl";
import { bootstrapCsrf } from "./lib/csrf";
import { FrontendApiError } from "./lib/problem";
import { DashboardPage } from "./pages/DashboardPage";
import { EditorPage } from "./pages/EditorPage";
import { RecentPage } from "./pages/RecentPage";
import { SharedPage } from "./pages/SharedPage";
import type { User } from "./types/user";

export const AUTH_CHECK_TIMEOUT_MS = 5000;

interface IamMeResponse {
  id: string;
  username: string;
  email: string;
  displayName: string;
}

function FullPageStatus({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-lg">{children}</div>
    </div>
  );
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<FrontendApiError | null>(null);

  useEffect(() => {
    let isActive = true;
    let timeoutId = 0;
    const timeout = new Promise<never>((_resolve, reject) => {
      timeoutId = window.setTimeout(
        () => reject(new DOMException("Auth check timed out", "AbortError")),
        AUTH_CHECK_TIMEOUT_MS
      );
    });

    Promise.race([bootstrapCsrf().then(() => api.get<IamMeResponse>("/api/iam/me")), timeout])
      .then((data) => {
        if (isActive) {
          setUser({ id: data.id, email: data.email, name: data.displayName || data.username });
        }
      })
      .catch((error: unknown) => {
        if (!isActive) return;
        if (error instanceof FrontendApiError) {
          if (error.status === 401) return;
          setAuthError(error);
          return;
        }
        setAuthError(
          new FrontendApiError(
            "Authentication bootstrap failed",
            503,
            error instanceof DOMException && error.name === "AbortError"
              ? "AUTH_BOOTSTRAP_TIMEOUT"
              : "AUTH_BOOTSTRAP_FAILED",
            true,
            ""
          )
        );
      })
      .finally(() => {
        window.clearTimeout(timeoutId);
        if (isActive) {
          setLoading(false);
        }
      });

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, []);

  // Protected routes enter the common Gateway login flow when no session exists.
  useEffect(() => {
    if (!loading && !user && !authError) {
      // Check if we're not on a public route (editor pages are public)
      const isPublicRoute = isPublicEditorPath(window.location.pathname);
      if (!isPublicRoute) {
        window.location.href = getGatewayNavigationUrl("login");
      }
    }
  }, [authError, loading, user]);

  // Hold all routes until auth resolves so an authenticated user never
  // flashes the standalone public editor.
  if (loading) {
    return <FullPageStatus>Loading...</FullPageStatus>;
  }

  if (authError?.status === 403) {
    return <FullPageStatus>접근 권한이 없습니다. ({authError.code})</FullPageStatus>;
  }

  if (authError?.status === 503 || authError?.retryable) {
    return (
      <FullPageStatus>
        인증 서비스를 일시적으로 사용할 수 없습니다. 잠시 후 다시 시도해 주세요.
      </FullPageStatus>
    );
  }

  if (authError) {
    return <FullPageStatus>인증 상태를 확인하지 못했습니다. ({authError.code})</FullPageStatus>;
  }

  const editorRoute = user ? (
    <Layout user={user}>
      <EditorPage shellMode="app" />
    </Layout>
  ) : (
    <EditorPage shellMode="standalone" />
  );

  return (
    <BrowserRouter>
      <Routes>
        {/* Public route - editor accessible without auth for public documents */}
        <Route path="/editor/:documentId" element={editorRoute} />

        {/* Protected routes - require authentication */}
        <Route
          path="/*"
          element={
            !user ? (
              <FullPageStatus>Redirecting to sign in...</FullPageStatus>
            ) : (
              <Layout user={user}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/dashboard/shared" element={<SharedPage />} />
                  <Route path="/dashboard/recent" element={<RecentPage />} />
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </Layout>
            )
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
